<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import type { DocumentRevision } from '@/entities/Document';
  import { approvalApi } from '@/features/approval';
  import { useVerificationId } from '@/features/verification';
  import { SimpleLoading, TableTitle } from '@/shared/ui';
  import {
    DocumentRevisionStatus,
    DocumentRevisionStatusShortLabels,
    formatCollaboratorNameAndRegistration,
    formatInstantToPtBR,
    useCustomToast,
  } from '@/shared/lib';
  import { formatDocumentPrefix, getDocumentRevisionStatusClasses } from '@/shared/lib/utils';
  import { ApprovalDetailsView, VerificationDetailsView } from '@/widgets/homologation-details';

  const route = useRoute();
  const router = useRouter();
  const toast = useCustomToast();

  const loading = ref(true);
  const errorMessage = ref<string | null>(null);
  const currentStatus = ref<DocumentRevisionStatus | null>(null);
  const approvalRevision = ref<DocumentRevision | null>(null);
  const {
    changelogLines: verificationChangelogLines,
    documentApprovalInfo: verificationDocumentApprovalInfo,
    documentElaborationInfo: verificationDocumentElaborationInfo,
    error: verificationError,
    fetchVerificationById,
    instructorsInfo: verificationInstructorsInfo,
    observationInfo: verificationObservationInfo,
    selectedCollaborators: verificationSelectedCollaborators,
    trainingInfo: verificationTrainingInfo,
    verification,
  } = useVerificationId({ redirectOnErrorTo: null });

  const revisionId = computed(() => Number(route.params.id));
  const routeStatus = computed(() => {
    const status = route.query.status;
    return typeof status === 'string' ? status : null;
  });
  const approvalChangelogLines = computed(
    () =>
      approvalRevision.value?.changelogEntries?.map((entry) => entry.description).filter(Boolean) ??
      []
  );
  const isApprovalStatus = computed(
    () => currentStatus.value === DocumentRevisionStatus.PENDING_APPROVAL
  );
  const isVerificationStatus = computed(
    () => currentStatus.value === DocumentRevisionStatus.PENDING_VERIFICATION
  );

  const statusBadgeText = computed(() =>
    currentStatus.value
      ? (DocumentRevisionStatusShortLabels[currentStatus.value] ?? currentStatus.value)
      : ''
  );
  const statusBadgeClass = computed(() =>
    currentStatus.value
      ? getDocumentRevisionStatusClasses(
          currentStatus.value,
          'px-3 py-1 rounded-full text-xs font-medium inline-flex items-center justify-center'
        )
      : ''
  );

  const formatRevision = (revision?: number | null) =>
    revision != null ? `Rev. ${String(revision).padStart(2, '0')}` : '-';

  const getDocumentId = (revision: DocumentRevision | null): string =>
    revision?.document?.id != null ? String(revision.document.id) : '';

  const getApprovalDocumentInfo = (revision: DocumentRevision | null) => {
    if (!revision) return [];

    return [
      {
        label: 'Data da solicitação',
        value: formatInstantToPtBR(revision.createdAt),
      },
      {
        label: 'Prefixo',
        value: formatDocumentPrefix(revision.document?.prefix),
      },
      {
        label: 'Revisão',
        value: formatRevision(revision.revision),
      },
      {
        label: 'Elaboração',
        value: formatCollaboratorNameAndRegistration(
          revision.document?.user?.name ?? revision.document?.createdBy?.name ?? null,
          revision.document?.user?.registrationNumber ?? null
        ),
      },
    ];
  };

  const approvalDocumentInfo = computed(() => getApprovalDocumentInfo(approvalRevision.value));

  const hasInternalBackTarget = () => {
    const historyState = router.options.history?.state as { back?: string | null } | null;
    return Boolean(historyState?.back);
  };

  const navigateBackOnUnavailableDocument = async (message: string) => {
    errorMessage.value = message;
    toast.showErrorToast({
      title: 'Erro',
      message,
      type: 'error',
    });

    if (hasInternalBackTarget()) {
      router.back();
      return;
    }

    await router.push('/homologacao');
  };

  const redirectToHomologation = async (message: string) => {
    errorMessage.value = message;
    toast.showErrorToast({
      title: 'Erro',
      message,
      type: 'error',
    });
    await router.push('/homologacao');
  };

  const loadDocument = async () => {
    if (!Number.isFinite(revisionId.value)) {
      await redirectToHomologation('Documento de homologação inválido.');
      return;
    }

    try {
      if (routeStatus.value === DocumentRevisionStatus.PENDING_APPROVAL) {
        const response = await approvalApi.getAwaitingApprovalById(revisionId.value);
        if (!response.data) {
          throw new Error('Empty approval response');
        }

        currentStatus.value = DocumentRevisionStatus.PENDING_APPROVAL;
        approvalRevision.value = response.data;
        return;
      }

      if (routeStatus.value === DocumentRevisionStatus.PENDING_VERIFICATION) {
        await fetchVerificationById(revisionId.value);
        if (verificationError.value || !verification.value?.id) {
          throw new Error(verificationError.value ?? 'Empty verification response');
        }

        currentStatus.value = DocumentRevisionStatus.PENDING_VERIFICATION;
        return;
      }

      try {
        const response = await approvalApi.getAwaitingApprovalById(revisionId.value);
        if (response.data) {
          currentStatus.value = DocumentRevisionStatus.PENDING_APPROVAL;
          approvalRevision.value = response.data;
          return;
        }
      } catch {
        // Fallback for direct route access without query status.
      }

      try {
        await fetchVerificationById(revisionId.value);
        if (verification.value?.id) {
          currentStatus.value = DocumentRevisionStatus.PENDING_VERIFICATION;
          return;
        }
      } catch {
        // Fallback for direct route access without query status.
      }

      await navigateBackOnUnavailableDocument(
        'Este documento não está mais disponível na homologação.'
      );
    } catch {
      await navigateBackOnUnavailableDocument(
        'Não foi possível carregar o documento da homologação.'
      );
    } finally {
      loading.value = false;
    }
  };

  onMounted(loadDocument);
</script>

<template>
  <div
    class="p-6"
    data-testid="homologation-document-page"
  >
    <div
      v-if="loading"
      class="w-full h-[80vh] flex justify-center items-center"
    >
      <SimpleLoading />
    </div>

    <div
      v-else-if="errorMessage"
      class="text-center text-red-500 py-6"
    >
      {{ errorMessage }}
    </div>

    <div
      v-else
      class="flex flex-col gap-6"
    >
      <div>
        <h1 class="text-gray-07 text-xl/7.5 font-semibold text-left mb-4">
          Homologação
        </h1>
        <span
          class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium"
          :class="statusBadgeClass"
        >
          {{ statusBadgeText }}
        </span>
      </div>

      <section
        class="overflow-x-auto bg-white-01 rounded-2xl border border-gray-02 divide-y divide-gray-02"
      >
        <div class="pl-6 py-4.5 w-full text-left">
          <TableTitle
            :text="isApprovalStatus ? 'Documento para Aprovação' : 'Documento para Verificação'"
          />
        </div>

        <template v-if="isApprovalStatus && approvalRevision">
          <ApprovalDetailsView
            :changelog="approvalChangelogLines"
            :document-id="getDocumentId(approvalRevision)"
            :info="approvalDocumentInfo"
          />
        </template>

        <template v-else-if="isVerificationStatus && verification">
          <VerificationDetailsView
            :changelog="verificationChangelogLines"
            :document-approval-info="verificationDocumentApprovalInfo"
            :document-elaboration-info="verificationDocumentElaborationInfo"
            :document-id="verification?.document?.id?.toString() as string"
            :instructors-info="verificationInstructorsInfo"
            :observation="verificationObservationInfo"
            :selected-collaborators="verificationSelectedCollaborators"
            :training-justification="verificationTrainingInfo.justification"
            :training-required="verificationTrainingInfo.required"
          />
        </template>
      </section>
    </div>
  </div>
</template>
